import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, Map, Users, UserRound, BookMarked, GraduationCap, MessageCircle, Sparkles, User, Shield, LogOut, BookOpen, ShieldCheck, Activity, Bookmark, Gavel, Search as SearchIcon, MessageSquarePlus } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import FeedbackModal from "./FeedbackModal";

const LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: Home, testid: "nav-dashboard" },
  { to: "/journey", label: "Journey", icon: Map, testid: "nav-journey" },
  { to: "/circles", label: "Circles", icon: Users, testid: "nav-circles" },
  { to: "/people", label: "People", icon: UserRound, testid: "nav-people" },
  { to: "/resources", label: "Resources", icon: BookMarked, testid: "nav-resources", soon: true },
  { to: "/mentors", label: "Mentors", icon: GraduationCap, testid: "nav-mentors" },
  { to: "/messages", label: "Messages", icon: MessageCircle, testid: "nav-messages" },
  { to: "/saved", label: "Saved", icon: Bookmark, testid: "nav-saved" },
  { to: "/search", label: "Search", icon: SearchIcon, testid: "nav-search" },
  { to: "/ai", label: "DocCircle AI", icon: Sparkles, testid: "nav-ai" },
];

const ADMIN_LINKS = [
  { to: "/trust-center", label: "Trust & Safety", icon: ShieldCheck, testid: "nav-trust-center" },
  { to: "/admin/ai-usage", label: "AI Usage", icon: Activity, testid: "nav-ai-usage" },
  { to: "/admin/feedback", label: "Feedback", icon: MessageSquarePlus, testid: "nav-admin-feedback" },
];

const ACCOUNT = [
  { to: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
  { to: "/privacy", label: "Privacy", icon: Shield, testid: "nav-privacy" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  // Combined unread badge — the sidebar is the ONE surface where users
  // see a trustworthy total across DMs, message requests, and Circle
  // group conversations. Value is derived server-side from authoritative
  // state so REST refresh / browser reload / reconnect all reconcile.
  const [msgTotal, setMsgTotal] = useState(0);
  // Phase 9B — realtime moderation notification badge (separate from
  // the messaging badge; muting NEVER suppresses it).
  const [modUnread, setModUnread] = useState(0);
  // Phase 10C — Moderation nav authority. Frontend visibility only;
  // backend continues to enforce `_can_moderate_circle` on every action.
  const [modAuthority, setModAuthority] = useState({ is_moderator: false, attention_count: 0 });
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await api.get("/messages/unread");
        if (!cancelled) setMsgTotal(r.data?.total ?? 0);
      } catch (_) { /* silent — sidebar keeps last known value */ }
      try {
        const r2 = await api.get("/moderation/my-notifications/unread-count");
        if (!cancelled) setModUnread(r2.data?.unread ?? 0);
      } catch (_) { /* silent */ }
      try {
        const r3 = await api.get("/moderation/authority");
        if (!cancelled) setModAuthority(r3.data || { is_moderator: false, attention_count: 0 });
      } catch (_) { /* silent — sidebar hides moderation nav */ }
    };
    fetchUnread();
    // WS side-channel — piggyback on the shared authenticated socket so
    // the badge updates immediately on any inbox-affecting event without
    // opening a second connection here.
    let ws;
    try {
      ws = new WebSocket(API.replace(/^http/, "ws") + "/ws/messaging");
      ws.onmessage = (ev) => {
        let d;
        try { d = JSON.parse(ev.data); } catch (_) { return; }
        if (d?.type === "message.new" || d?.type === "group.message.new") {
          fetchUnread();
        }
        if (d?.type === "moderation.notification.new") {
          // Re-fetch authoritative unread — WS is transport only.
          fetchUnread();
          // Privacy-safe copy — no evidence / case content shown here.
          try { toast("A moderation decision is available."); } catch (_) {}
        }
        if (d?.type === "moderation.case.updated") {
          // Refresh the attention badge; authority derives from Mongo.
          fetchUnread();
        }
      };
    } catch (_) { /* silent */ }
    const poll = setInterval(fetchUnread, 30000);
    return () => {
      cancelled = true;
      clearInterval(poll);
      try { ws && ws.close(); } catch (_) {}
    };
  }, [user]);
  return (
    <aside className="hidden md:flex md:w-64 lg:w-72 shrink-0 flex-col border-r border-border bg-card min-h-screen" data-testid="sidebar">
      <div className="p-6 border-b border-border">
        <div className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-primary" />
          <span className="font-serif text-2xl font-black tracking-tight">DocCircle</span>
        </div>
      </div>
      <nav className="flex-1 px-3 py-6 space-y-1">
        {LINKS.map(({ to, label, icon: Icon, testid, soon }) => (
          <NavLink key={to} to={to} data-testid={testid}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              isActive ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"}`}
          >
            <Icon className="h-4 w-4" />
            <span className="flex-1">{label}</span>
            {to === "/messages" && msgTotal > 0 && (
              <span
                className="inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-medium px-1.5"
                data-testid="nav-messages-badge"
                title="Unread messages"
              >
                {msgTotal > 99 ? "99+" : msgTotal}
              </span>
            )}
            {to === "/trust-center" && modUnread > 0 && (
              <span
                className="inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-medium px-1.5"
                data-testid="nav-mod-badge"
                title="Moderation notifications"
              >
                {modUnread > 99 ? "99+" : modUnread}
              </span>
            )}
            {soon && <span className="text-[9px] uppercase tracking-widest text-primary/70">soon</span>}
          </NavLink>
        ))}
        {modAuthority.is_moderator && (
          <NavLink
            to="/moderation-dashboard"
            data-testid="nav-moderation"
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              isActive ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"}`}
          >
            <Gavel className="h-4 w-4" />
            <span className="flex-1">Moderation</span>
            {modAuthority.attention_count > 0 && (
              <span
                className="inline-flex min-w-[20px] h-5 items-center justify-center rounded-full bg-amber-600 text-white text-[10px] font-medium px-1.5"
                data-testid="nav-moderation-badge"
                title="Open + Under Review cases"
              >
                {modAuthority.attention_count > 99 ? "99+" : modAuthority.attention_count}
              </span>
            )}
          </NavLink>
        )}
        <p className="pt-6 pb-2 px-3 text-[10px] uppercase tracking-widest text-muted-foreground">Account</p>
        {ACCOUNT.map(({ to, label, icon: Icon, testid }) => (
          <NavLink key={to} to={to} data-testid={testid}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              isActive ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
        {user && (user.role === "admin" || user.role === "faculty") && (
          <>
            <p className="pt-6 pb-2 px-3 text-[10px] uppercase tracking-widest text-muted-foreground">Administration</p>
            {ADMIN_LINKS.map(({ to, label, icon: Icon, testid }) => (
              <NavLink key={to} to={to} data-testid={testid}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-secondary-foreground"}`}>
                <Icon className="h-4 w-4" /> {label}
              </NavLink>
            ))}
          </>
        )}
      </nav>
      <div className="border-t border-border p-4 space-y-3">
        {user && (
          <div className="text-xs">
            <p className="font-medium truncate">{user.display_name || user.name}</p>
            <p className="text-muted-foreground truncate">{user.degree_type || user.program} · {user.discipline || user.field_of_study || "—"}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="sm"
          data-testid="nav-send-feedback"
          className="w-full justify-start text-muted-foreground hover:text-foreground"
          onClick={() => setFeedbackOpen(true)}
        >
          <MessageSquarePlus className="h-4 w-4 mr-2" /> Send feedback
        </Button>
        <Button variant="outline" size="sm" data-testid="logout-btn" className="w-full justify-start"
          onClick={async () => { await logout(); nav("/login"); }}>
          <LogOut className="h-4 w-4 mr-2" /> Sign out
        </Button>
      </div>
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </aside>
  );
}
